/**
 * The words of the site in English, the source every other language is made from. Each area of the site has its own
 * group. A sentence with a value in it uses braces, `{date}`, so a language can put the value where its own order wants.
 * Names of things (Maschina, Solana, Söhne, PostHog) are not translated.
 */
export const en = {
	/** The names of pages, menus and columns, by their English name. A name with no entry here is shown as it is. */
	labels: {
		Product: "Product",
		"Explore Maschina": "Explore Maschina",
		"See It Work": "See It Work",
		Overview: "Overview",
		Security: "Security",
		Marketplace: "Marketplace",
		Network: "Network",
		Mobile: "Mobile",
		"Live Record": "Live Record",
		Changelog: "Changelog",
		Developers: "Developers",
		"Build on Maschina": "Build on Maschina",
		"For Builders and Agents": "For Builders and Agents",
		Docs: "Docs",
		API: "API",
		SDK: "SDK",
		"Machine Spec": "Machine Spec",
		Templates: "Templates",
		GitHub: "GitHub",
		Status: "Status",
		"AI Index": "AI Index",
		"Skill File": "Skill File",
		"MCP Server": "MCP Server",
		Company: "Company",
		"The Company": "The Company",
		Follow: "Follow",
		About: "About",
		Roadmap: "Roadmap",
		Contact: "Contact",
		X: "X",
		"How It Works": "How It Works",
		Machines: "Machines",
		Pricing: "Pricing",
		Papers: "Papers",
		Support: "Support",
		"Brand Assets": "Brand Assets",
		Terms: "Terms",
		Privacy: "Privacy",
		"Risk Disclosure": "Risk Disclosure",
		"Acceptable Use": "Acceptable Use",
		License: "License",
		"Security Disclosure": "Security Disclosure",
		Quickstart: "Quickstart",
		Examples: "Examples",
		Resources: "Resources",
		Legal: "Legal",
		Preferences: "Preferences",
		Site: "Site",
		"Maschina on X": "Maschina on X",
		"Maschina on GitHub": "Maschina on GitHub",
		"Maschina on YouTube": "Maschina on YouTube",
	} as Record<string, string>,
	header: {
		home: "Maschina, home",
		pages: "Pages",
		search: "Search",
		searchMaschina: "Search Maschina",
		noMatch: "No pages match.",
		menu: "Menu",
		pagesMenu: "Pages, menu",
		login: "Sign In",
		tryMaschina: "Try Maschina",
		soon: "Soon",
	},
	footer: {
		sitemap: "Sitemap",
		soon: "Soon",
		rights: "© {year} Maschina, Corp.",
		status: {
			checking: "Checking",
			up: "All systems normal",
			paused: "Under maintenance",
			down: "Some systems down",
		},
	},
	preferences: {
		title: "Cookie preferences",
		body: "Essential cookies keep you signed in and always stay on. Optional cookies let us use PostHog to see which pages you open, so we can improve Maschina. Nothing else is turned on.",
		choice: "Your choice: {choice}",
		accepted: "accepted",
		declined: "declined",
		decline: "Decline",
		accept: "Accept",
	},
	home: {
		capabilities: {
			name: "What you can build",
			title: "Different jobs. One system.",
			line: "A machine is software with a job, a wallet, and defined authority. Trading is the first working use case; data, research, and compute are part of the wider direction.",
			link: "Explore machine kinds",
			groups: [
				{
					status: "Built today",
					title: "Put a strategy to work.",
					body: "Run a defined trading strategy with settings you choose and activity you can inspect.",
					items: ["Fixed range", "Following range", "Price trigger", "Recurring buy"],
				},
				{
					status: "Planned capabilities",
					title: "Build beyond trading.",
					body: "The wider platform is intended for machines that produce useful work and provide services to other machines.",
					items: ["Data services", "Public-source research", "Bounded compute jobs"],
				},
			],
		},
		ecosystem: {
			networkBody:
				"The planned network will connect machines with computers that can run their jobs. Owners define the work; operators provide the execution environment.",
			globeNote:
				"The globe illustrates the network's intended reach. It does not show live operators or locations.",
			networkLink: "Explore the network",
			marketBody:
				"The planned marketplace will bring machine creators and owners together around useful jobs, declared capabilities, and inspectable work records.",
			examplesNote:
				"Illustrative machine types. The marketplace is not open. Data, research, and compute services shown here are planned capabilities.",
			marketLink: "Explore the marketplace",
		},
		workbench: {
			pause: "Pause code motion",
			resume: "Resume code motion",
			name: "For builders",
			title: "Look inside the machine.",
			line: "Inspect the interfaces behind decisions, machine kinds, and owner-defined limits. These excerpts come directly from the source.",
			choose: "Explore the source",
			docs: "Read the SDK guide",
			source: "Explore the repository",
			availability:
				"The SDK is a private workspace package. Work from the repository; it is not published to npm.",
			license: "Source-available. Review the repository license before using the code.",
			samples: [
				{
					title: "Decisions",
					body: "A decision proposes an action or explains why it skipped. Returning a proposal does not sign a transaction.",
				},
				{
					title: "Machine kinds",
					body: "A kind defines its settings and decision function. Defining one does not deploy it or add it to the hosted app.",
				},
				{
					title: "Limits",
					body: "The runtime represents the owner's spending caps, approved tokens, recipients, and execution limits explicitly.",
				},
			],
		},
		workflow: {
			name: "How it works",
			title: "Give a machine a job.",
			line: "Start with a defined task, choose how it operates, and follow the work it does.",
			docs: "Read the quickstart",
			steps: [
				{
					title: "Choose what it does.",
					body: "Pick a machine kind, such as a range strategy, a price trigger, or a recurring buy. Each follows a defined set of instructions.",
				},
				{
					title: "Set how it operates.",
					body: "Choose the market and settings for that job, then define its budget. The machine uses a separate wallet from your personal one.",
				},
				{
					title: "Run it and follow the record.",
					body: "Try paper mode against live prices before committing funds, or fund the machine for real trades. Review its activity and stop it when you choose.",
				},
			],
		},
		evidence: {
			name: "Verify the record",
			title: "See what actually happened.",
			line: "Inspect completed real-money trades and follow each transaction on Solana. The public record also keeps paper activity and refused actions separate.",
			context:
				"These are founder-funded trades from an early product. An execution record does not establish profitability or replace an independent security audit.",
			latest: "Latest real-money trades",
			open: "Explore the public record",
			loading: "Loading the public record…",
			unavailable: "The public record is temporarily unavailable.",
			retry: "Try again",
			empty: "No completed real-money trades are available yet.",
			buy: "Bought SOL",
			sell: "Sold SOL",
			transaction: "Verify transaction",
			note: "Actual recorded amounts, not simulated results or a returns forecast.",
		},
		sectionLinks: {
			next: "Up next",
			product: "Explore the product",
			controls: "Understand the controls",
			evidence: "Inspect the evidence",
			help: "Before you begin",
			productLine: "See what is available",
			howLine: "Understand the workflow",
			docsLine: "Read the guides",
			securityLine: "Review the safeguards",
			riskLine: "Understand the risks",
			recordLine: "Inspect recorded activity",
			codeLine: "Read the source",
			statusLine: "Check service health",
			pricingLine: "Understand the costs",
			supportLine: "Find an answer",
			contactLine: "Get in touch",
		},
		controls: {
			name: "Your controls",
			title: "You define the boundaries.",
			line: "Keep authority with the owner: define the job, control its budget, and decide when it runs. Explore these controls through a range-machine example.",
			previous: "Previous control",
			next: "Next control",
			cards: "Explore machine controls",
			rangeTitle: "Choose the range.",
			rangeBody:
				"Set the price band for a range machine. Adjust its width to explore a tighter or wider range.",
			rangeLabel: "Range width",
			rangeVisual: "Price band around your chosen center",
			amountTitle: "Set the trade amount.",
			amountBody:
				"Define how much USDC the machine uses for a buy. Its available budget is checked before a trade is signed.",
			amountLabel: "Amount per buy",
			stopTitle: "Decide when it stops.",
			stopBody:
				"Stop the machine to prevent new trades. Stopping does not reverse a trade that has already been submitted.",
			pause: "Pause example",
			resume: "Resume example",
			paused: "Paused",
			running: "Running",
			sample: "Interactive examples. These controls do not configure a real machine or move funds.",
		},
		proof: {
			name: "Built on evidence",
			since: "28 Sep 2026",
			trading: "First founder-funded trade on Solana",
			chain: "On chain",
			record: "Check real trades in the public record",
			languages: "Interface languages",
			source: "Source-available",
			inspect: "Inspect the code and its license",
		},
		paper: {
			name: "Paper mode",
			title: "Try it before you fund it.",
			titleBottom: "Practice without funding.",
			priceLabel: "SOL / USDT market price",
			bandLabel: "Your paper price band",
			line: "Run a paper machine against live market prices and follow its decisions without committing funds. Paper activity stays separate from real trades.",
			start: "Try paper mode",
			caption:
				"Adjust the band around live SOL prices to explore a range setting. This interactive illustration does not run a machine or execute trades.",
			band: {
				width: "Band width",
				center: "Move the band",
				inside: "Price is inside your band.",
				outside: "Price is outside your band.",
				lower: "Lower bound",
				upper: "Upper bound",
				reset: "Center on price",
			},
			loading: "Loading SOL market prices…",
			unavailable: "Market prices are unavailable. Please try again later.",
			connected: "Live market feed connected. Paper trades do not move funds.",
			disconnected: "Historical prices shown. Live market feed is disconnected.",
		},
		destinations: {
			name: "Explore Maschina",
			machines: "Give a machine a job",
			network: "Explore the network",
			marketplace: "Discover the marketplace",
		},
		hero: {
			name: "Maschina",
			headlineTop: "The operating system",
			headlineBottom: "for the machine economy.",
			line: "Build machines that act on your behalf, with a wallet, a job, and rules you define. Follow what they do and stay in control as they work.",
			start: "Start for free",
			docs: "Read the docs",
			facts: "Source-available, built on Solana.",
		},
		showcase: {
			name: "The App",
			title: "Everything in one place.",
			alt: "The home screen of the Maschina app, shown full screen: a candlestick chart of SOL with a trade marked on it, a curve of realized profit, and tiles for the machine that is running, its latest decision, the vault balance and a new machine.",
			caption: "The Maschina app, shown with sample data.",
		},
		loop: {
			name: "The machine loop",
			eyebrow: "FROM MACHINE TO MARKET",
			title: "Build a machine. Connect it to the network.",
			line: "Start with a machine you configure and control. The network is the next layer: a place for machines to find one another and exchange value.",
			previous: "Previous step",
			next: "Next step",
			steps: [
				{
					number: "01",
					name: "BUILD",
					title: "Give it a job.",
					line: "Choose what the machine does, then set its strategy, budget, and limits.",
				},
				{
					number: "02",
					name: "OPERATE",
					title: "Keep the rules yours.",
					line: "Its own wallet gives the machine room to act. Your budgets and permissions bound what it can do.",
				},
				{
					number: "03",
					name: "CONNECT",
					title: "A market made for machines.",
					line: "The network is designed to let machines discover one another and exchange value as the market takes shape.",
				},
			],
		},
		how: {
			name: "How It Works",
			title: "The machine proposes. Your rules decide.",
			line: "Each proposed action is checked against the wallet permissions and limits you set. Approved actions reach the signer and settle on Solana. Rejected actions stop before signing.",
			steps: [
				{
					title: "Pick a kind",
					body: "Choose what the machine does, such as trading SOL. It gets a wallet of its own.",
				},
				{
					title: "Set its limits",
					body: "Decide how much it may spend and which tokens it may touch. The limits are yours, and the AI cannot change them.",
				},
				{
					title: "Let it work",
					body: "Every trade is checked against your limits before it moves, and every decision is written to the record.",
				},
			],
			primary: "Make a machine",
			secondary: "Read the docs",
		},
		inside: {
			name: "Inside the App",
			title: "See every move it makes.",
			line: "Your portfolio, every decision, every trade, and a manager you can ask, all in one app.",
			tiles: [
				{
					title: "Portfolio",
					body: "Your machines and their wallets, with the balance and the result of each.",
				},
				{
					title: "Decisions",
					body: "What each machine decided and why, including the trades it refused.",
				},
				{
					title: "Manager",
					body: "An assistant you can ask about your machines, running on your own AI key.",
				},
				{
					title: "Record",
					body: "An append-only record of everything a machine does, kept so it can be checked.",
				},
			],
			sample: "Sample data.",
			primary: "Try it on paper",
		},
		safety: {
			name: "Safety",
			title: "The limits are yours.",
			line: "A machine can only spend what you give it, inside the limits you set. Every trade is checked against them before it is signed.",
			flow: ["The AI proposes", "Your limits check it", "The signer signs", "Solana settles it"],
			points: [
				"Budgets live in a ledger the machine cannot edit.",
				"Every action is checked before anything is signed.",
				"A refused action is recorded too, with the reason.",
				"Maschina never holds your own wallet's keys.",
			],
			audit:
				"Maschina is early and has not been independently audited. Read the {link} for what can go wrong.",
			auditLink: "risk disclosure",
			primary: "Read how it is built",
		},
		customize: {
			name: "Customize",
			title: "Make it yours.",
			line: "Set the range, the floor and the limits on every machine. Arranging your own screens and tiles is coming soon.",
			settings: ["Range", "Floor", "Amount per trade", "Daily limit"],
			sample: "Sample settings.",
			primary: "Make a machine",
		},
		live: {
			name: "Live Record",
			title: "Every decision, on the record.",
			open: "Open the live record",
			source: "Live · public gateway",
			loading: "Reading the live record…",
			unavailable: "The live record is unavailable here right now.",
			empty: {
				live: "No finished live trades yet.",
				refused: "No refused actions in this record.",
				paper: "No paper trades in this record yet.",
			},
			note: "Live, refused and paper entries are shown separately. Paper activity is never counted as live trading.",
			tabs: { live: "Live", refused: "Refused", paper: "Paper" },
			columns: {
				time: "Time",
				side: "Side",
				sol: "SOL",
				usdc: "USDC",
				proof: "Transaction",
				gate: "Stopped by · rule",
				execution: "Execution",
			},
		},
		network: {
			name: "Network",
			title: "A network of computers, running machines.",
			line: "Soon, anyone will be able to lend a computer to run machines, and be paid for it.",
			tag: "Coming soon",
			globe: "The network, on a turning globe",
		},
		marketplace: {
			name: "Marketplace",
			title: "Find machines by their record.",
			line: "Review a machine's task, limits, and work record before you choose to copy it or hire it for a job.",
			tag: "Coming soon",
			examplesLabel: "Example machine types",
			exampleLabel: "Example machine",
			outputLabel: "Produces",
			recordLabel: "Work record",
			examples: [
				{
					category: "Trading",
					name: "Range strategy",
					visual: "Range",
					description: "Acts on market prices within rules set by its owner.",
					output: "Trading decisions",
					record: "Decisions and refusals",
				},
				{
					category: "Data",
					name: "Market data service",
					visual: "Data",
					description: "Packages price and liquidity observations for other machines to use.",
					output: "Structured market data",
					record: "Requests and deliveries",
				},
				{
					category: "Research",
					name: "Public-source researcher",
					visual: "Research",
					description: "Collects public information and returns it in a structured format.",
					output: "Research results",
					record: "Sources and job results",
				},
				{
					category: "Compute",
					name: "Compute worker",
					visual: "Compute",
					description: "Runs a bounded job for another machine and returns the result.",
					output: "Completed work",
					record: "Requests and results",
				},
			],
		},
		builders: {
			name: "For Builders",
			title: "Read every line.",
			line: "Maschina is source-available. Read the code and see exactly what a machine can and cannot do.",
			view: "View the code",
			docs: "Read the docs",
		},
		faq: {
			name: "Questions",
			title: "Before you start.",
			items: [
				{
					question: "What is Maschina?",
					answer:
						"Maschina lets you create machines with a wallet and a defined job. Today, you can build trading machines on Solana, set their budgets, and follow their activity.",
				},
				{
					question: "What can a machine do with my money?",
					answer:
						"A machine uses a separate wallet funded with the amount you allocate. Its trading budget is checked before signing, and wallet policies restrict transfers. Maschina does not need the private keys to your personal wallet.",
				},
				{
					question: "What if it makes a bad trade?",
					answer:
						"A trade can meet your rules and still lose money. Budget checks constrain spending; they do not guarantee a good price or a profit. You can stop a machine to prevent further trades.",
				},
				{
					question: "Do I need money to try it?",
					answer:
						"No. Paper mode lets you test a machine against live market prices without funding a wallet or submitting real trades. Paper results are kept separate from real-money activity.",
				},
				{
					question: "Is it live with real money?",
					answer:
						"Yes. Range Finder began trading founder-funded capital on Solana on September 28, 2026. Its real trades can be checked on chain. This is an early operating record, not evidence of profitability.",
				},
				{
					question: "Can I read the code?",
					answer:
						"Yes. The source is available to inspect, including the runtime and signing checks. The repository license explains how you can use it.",
				},
			],
		},
		closing: {
			name: "Get Started",
			title: "Ready to build your first machine?",
			line: "Test it on paper first, with no money at risk.",
			start: "Start for free",
			docs: "Read the docs",
		},
	},
	notFound: {
		code: "404",
		title: "Page Not Found",
		body: "There is nothing at this address. It may have moved, or the link may be wrong.",
		home: "Go to the front page",
	},
	contact: {
		kicker: "Company",
		title: "Contact",
		standfirst: "Tell us what you need, and it goes to the right inbox.",
		who: "Who is it for",
		topics: {
			support: { label: "Support", hint: "Help using Maschina" },
			billing: { label: "Billing", hint: "A charge or a fee" },
			legal: { label: "Legal", hint: "Terms, privacy, license" },
			abuse: { label: "Abuse", hint: "Report a breach of the rules" },
		},
		name: "Your name",
		email: "Your email",
		message: "Your message",
		send: "Send message",
		sending: "Sending",
		sentTitle: "Message sent.",
		sentBody: "Replies go to the address you gave.",
		securityNote: "A security problem? Use the {link} page instead.",
		securityLink: "security disclosure",
		problems: {
			invalid:
				"Check the fields: the email needs to be a real address, and the message a few words at least.",
			limit: "Too many messages from this address. Please wait a minute and try again.",
			unavailable: "Your message cannot be sent right now. Write to support@maschina.dev instead.",
			network:
				"Your message cannot be sent right now. Check your connection, or write to support@maschina.dev instead.",
		},
	},
	legal: {
		label: "Legal",
		updated: "Last updated {date}",
		onThisPage: "On this page",
		translation:
			"This is a translation. If it differs from the {link}, the English version applies.",
		englishVersion: "English version",
	},
	article: {
		by: "By {name}",
	},
	papers: {
		soon: "Coming soon",
	},
	brand: {
		svg: "SVG",
		png: "PNG",
		pack: "Full asset pack",
		clearspaceWordmark: "x = the height of the wordmark",
		clearspaceMark: "x = half the height of the mark",
	},
	language: {
		label: "Language",
		soon: "Soon",
	},
	// Last, on purpose: a string added anywhere else would move every string after it.
	downloads: {
		papers: "Download everything",
	},
} as const;
